import { MousePointerClick, ShoppingBag, Wallet } from "lucide-react";
import { useEffect, useState } from "react";
import {
  Bar, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

import { divulgacaoApi } from "./api";
import { TileMarca } from "./CardLink";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { reais } from "@/features/disparos/sms/api";
import { CarregandoBloco, CarregandoCards } from "@/components/carregando";


const PERIODOS = [
  { dias: 7, rotulo: "7 dias" },
  { dias: 15, rotulo: "15 dias" },
  { dias: 30, rotulo: "30 dias" },
  { dias: 90, rotulo: "90 dias" },
];

const diaBR = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

function TooltipDia({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border bg-card px-3 py-2 text-xs shadow-lg space-y-0.5">
      <p className="font-semibold">{diaBR(d.dia)}</p>
      <p className="text-sky-500">{d.cliques} clique{d.cliques === 1 ? "" : "s"}</p>
      <p className="text-violet-500">{d.pedidos} pedido{d.pedidos === 1 ? "" : "s"}</p>
      <p className="text-emerald-500">{reais(d.receita_centavos)}</p>
    </div>
  );
}

/**
 * Popup de metricas de UM link (pedido do dono): grafico diario de cliques e
 * pedidos com filtro de periodo, totais do periodo e acumulado desde a criacao.
 */
export default function ModalMetricasLink({ link, tile, titulo, onFechar }) {
  const [dias, setDias] = useState(30);
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);

  useEffect(() => { if (link) { setDias(30); setDados(null); setErro(null); } }, [link]);
  useEffect(() => {
    if (!link) return;
    setDados(null);
    divulgacaoApi.metricas(link.track_token, dias).then(setDados)
      .catch(() => setErro("Não foi possível carregar as métricas."));
  }, [link, dias]);

  const t = dados?.totais_periodo;
  const acum = dados?.desde_criacao;

  return (
    <Dialog open={!!link} onOpenChange={(v) => { if (!v) onFechar(); }}>
      <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-2xl rounded-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5">
            <TileMarca slug={tile} />
            Métricas — {titulo}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-1">
          <div className="flex flex-wrap gap-1.5">
            {PERIODOS.map((p) => (
              <button key={p.dias} onClick={() => setDias(p.dias)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  dias === p.dias ? "bg-primary text-primary-foreground border-primary"
                    : "hover:border-primary text-muted-foreground"}`}>
                {p.rotulo}
              </button>
            ))}
          </div>

          {erro && <p className="text-sm text-destructive">{erro}</p>}
          {!dados && !erro && (
            // Carregamento com cards: os 3 números e o gráfico piscando.
            <>
              <CarregandoCards quantos={3} className="grid grid-cols-2 gap-2 [&>*:last-child]:col-span-2" altura="h-[64px]" />
              <CarregandoBloco altura="h-[262px]" />
            </>
          )}

          {dados && (<>
            {/* Sem cortar número grande (26/10/2026): cliques e pedidos lado a
                lado e o faturado na linha inteira — em 3 colunas "R$ 12.345.678,90"
                virava "R$ 1…" */}
            <div className="grid grid-cols-2 gap-2">
              {[
                { Icone: MousePointerClick, cor: "text-sky-500 bg-sky-500/10", valor: t.cliques.toLocaleString("pt-BR"), rotulo: "cliques no período" },
                { Icone: ShoppingBag, cor: "text-violet-500 bg-violet-500/10", valor: t.pedidos.toLocaleString("pt-BR"), rotulo: "pedidos no período" },
                { Icone: Wallet, cor: "text-emerald-500 bg-emerald-500/10", valor: reais(t.receita_centavos), rotulo: "faturado no período" },
              ].map(({ Icone, cor, valor, rotulo }, i) => (
                <div key={rotulo} className={`rounded-xl border bg-card px-3 py-2.5 flex items-center gap-2.5 min-w-0 ${i === 2 ? "col-span-2" : ""}`}>
                  <span className={`rounded-lg p-2 shrink-0 ${cor}`}><Icone className="w-4 h-4" /></span>
                  <div className="min-w-0">
                    <p className="text-base sm:text-lg font-bold tabular-nums leading-tight" data-e2e="metrica-periodo">{valor}</p>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{rotulo}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-xl border bg-card p-3">
              <ResponsiveContainer width="100%" height={220}>
                <ComposedChart data={dados.serie} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                  <XAxis dataKey="dia" tickFormatter={diaBR} tick={{ fontSize: 10 }}
                    interval="preserveStartEnd" tickLine={false} axisLine={false} minTickGap={24} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                  <Tooltip content={<TooltipDia />} cursor={{ fill: "rgba(120,120,140,0.08)" }} />
                  <Bar dataKey="cliques" name="Cliques" fill="#0ea5e9" radius={[3, 3, 0, 0]} maxBarSize={26} />
                  <Bar dataKey="pedidos" name="Pedidos" fill="#8b5cf6" radius={[3, 3, 0, 0]} maxBarSize={26} />
                </ComposedChart>
              </ResponsiveContainer>
              <div className="flex items-center gap-4 justify-center mt-1 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-sky-500" /> Cliques</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-violet-500" /> Pedidos</span>
              </div>
            </div>

            <p className="text-[11px] text-muted-foreground">
              Desde a criação: <strong className="text-foreground tabular-nums">{acum.cliques.toLocaleString("pt-BR")}</strong> cliques ·{" "}
              <strong className="text-foreground tabular-nums">{acum.pedidos.toLocaleString("pt-BR")}</strong> pedidos ·{" "}
              <strong className="text-foreground tabular-nums">{reais(acum.receita_centavos)}</strong> faturados
            </p>
          </>)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
